import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, Outlet, useLocation } from 'react-router'
import { useSession } from '../session'
import { t } from '../i18n'
import { Icon, type IconName } from '../ui/Icon'
import { CAPTIONS_PATH, MAP_PATH, NODES_PATH, ROUTES_PATH, TRANSCODERS_PATH } from '../admin/network/api'
import { LicenseBanner } from '../admin/LicenseBanner'
import { AppsButton } from './AppsMenu'
import { AccountMenu } from './AccountMenu'
import { ConsoleBrand } from './Brand'

/*
 * The console's shell (control., every /admin/* page; AUDIT CN-01): the system's console frame —
 * 11 «Shell», ScreenNetworkMap, LayoutBreakpoints › «Console and shared header».
 *
 * - From 1280 px: 48 px top bar (logo + CONTROL, breadcrumb, the right-hand block) and the 220 px
 *   rail (.sx-rail) on the left.
 * - 768–1279: the rail's items become the header's tabs in one line, scrolling sideways; the right-hand
 *   block stays on the first line (F-22, RD-F did the same for the cabinet header).
 * - Up to 767: phone layout; the rail becomes the header's menu (a button with the current section).
 *
 * The items are the console's tabs as before, in the same order and on the same routes (no new
 * sections). The system's rail has Network (Nodes · Map · Routes · Incidents) · Services (Captions ·
 * Transcoders) · Account (Operators · Licence); our tabs that it does not name take the nearest place
 * (AUDIT CN-18): Overview stands first without a group, Users · Streams · Outputs · Log · Status page ·
 * Settings go to Account before License. Counts beside the items, the incident count and the fleet
 * totals of the top bar wait for the owner's answer (they need new requests on every page, CN-18).
 *
 * Test ids of the old tabs stay on their items: nav-network (the Network group, data-current while
 * a /admin/network page is open), network-nav (the items under /admin/network: Nodes · Map · Routes ·
 * Captions · Transcoders, the Network and Services groups), nav-status-page, nav-settings, nav-license,
 * nav-cabinet. One navigation is rendered at a time, so each id stays unique.
 */

interface Item {
  to: string
  label: string
  icon: IconName
  testId?: string
  /** other paths this item stands for (a node group is opened from Nodes) */
  also?: string[]
  exact?: boolean
}
interface Group {
  key: string
  label?: string
  items: Item[]
}

const OVERVIEW: Group = { key: 'overview', items: [{ to: '/admin', label: 'Overview', icon: 'target', exact: true }] }
const NETWORK: Group = {
  key: 'network',
  label: 'console.rail.network',
  items: [
    { to: NODES_PATH, label: 'Nodes', icon: 'node', also: ['/admin/network/groups'] },
    { to: MAP_PATH, label: 'Map', icon: 'map' },
    { to: ROUTES_PATH, label: 'Routes', icon: 'route' },
  ],
}
const SERVICES: Group = {
  key: 'services',
  label: 'console.rail.services',
  items: [
    { to: CAPTIONS_PATH, label: 'Captions', icon: 'captions' },
    { to: TRANSCODERS_PATH, label: 'Transcoders', icon: 'transcode' },
  ],
}
// DS: CN-18 — Users, Streams, Outputs, Log, Status page and Settings have no place in the system's rail.
const ACCOUNT: Group = {
  key: 'account',
  label: 'console.rail.account',
  items: [
    { to: '/admin/users', label: 'Users', icon: 'user' },
    { to: '/admin/streams', label: 'Streams', icon: 'signal' },
    { to: '/admin/outputs', label: 'Outputs', icon: 'outputs' },
    { to: '/admin/audit', label: 'Log', icon: 'lock' },
    { to: '/admin/status', label: 'Status page', icon: 'incident', testId: 'nav-status-page' },
    { to: '/admin/settings', label: 'Settings', icon: 'tune', testId: 'nav-settings' },
    { to: '/admin/license', label: 'License', icon: 'plan', testId: 'nav-license' },
  ],
}
const NETWORK_PREFIX = '/admin/network'

const under = (path: string, to: string) => path === to || path.startsWith(`${to}/`)
const isOn = (it: Item, path: string) => (it.exact ? path === it.to : under(path, it.to) || !!it.also?.some((p) => under(path, p)))

/** The open page's item and its group, for the breadcrumb and the phone menu's button. */
function current(path: string): { group?: Group; item?: Item } {
  for (const g of [OVERVIEW, NETWORK, SERVICES, ACCOUNT]) {
    const item = g.items.find((it) => isOn(it, path))
    if (item) return { group: g, item }
  }
  return {}
}

type Mode = 'rail' | 'tabs' | 'menu'

/** ≥ 1280 rail, 768–1279 header tabs, ≤ 767 the header's menu (LayoutBreakpoints F-22). */
function useMode(): Mode {
  const read = (): Mode =>
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? 'rail'
      : window.matchMedia('(min-width: 1280px)').matches
        ? 'rail'
        : window.matchMedia('(min-width: 768px)').matches
          ? 'tabs'
          : 'menu'
  const [mode, setMode] = useState<Mode>(read)
  useEffect(() => {
    const qs = ['(min-width: 1280px)', '(min-width: 768px)'].map((q) => window.matchMedia(q))
    const on = () => setMode(read())
    for (const q of qs) q.addEventListener('change', on)
    return () => qs.forEach((q) => q.removeEventListener('change', on))
  }, [])
  return mode
}

function RailLink({ it, path, tab }: { it: Item; path: string; tab?: boolean }) {
  const on = isOn(it, path)
  if (tab) {
    // the system's top-bar tab (.sx-top nav a): selection = accent tint + 2 px accent edge below
    return (
      <Link
        to={it.to}
        data-testid={it.testId}
        aria-current={on ? 'page' : undefined}
        className={`inline-flex h-8 flex-none items-center whitespace-nowrap rounded-md px-3 text-[13px] ${on ? 'bg-accent/10 text-text shadow-[inset_0_-2px_0_var(--accent)]' : 'text-muted hover:text-text'}`}
      >
        {it.label}
      </Link>
    )
  }
  return (
    <Link to={it.to} data-testid={it.testId} aria-current={on ? 'page' : undefined} className={`${on ? 'on' : ''} max-md:!h-11`}>
      <Icon name={it.icon} size={16} />
      {it.label}
    </Link>
  )
}

/** The group's heading; Network keeps the old tab's test id and says when a Network page is open. */
function GroupHead({ g, path, tab }: { g: Group; path: string; tab?: boolean }) {
  if (!g.label) return null
  const network = g.key === 'network'
  const attrs = network ? { 'data-testid': 'nav-network', 'data-current': under(path, NETWORK_PREFIX) ? 'true' : 'false' } : {}
  if (tab) {
    return (
      <span {...attrs} className="sr-only">
        {t(g.label as 'console.rail.network')}
      </span>
    )
  }
  return (
    <div className="grp" {...attrs}>
      {t(g.label as 'console.rail.network')}
    </div>
  )
}

/** All items in their groups: the rail, the header's tabs or the phone menu's list. */
function NavItems({ path, tab }: { path: string; tab?: boolean }) {
  const group = (g: Group) => [
    <GroupHead key={`${g.key}-h`} g={g} path={path} tab={tab} />,
    ...g.items.map((it) => <RailLink key={it.to} it={it} path={path} tab={tab} />),
  ]
  const sep = (key: string) => (tab ? <span key={key} aria-hidden className="mx-1 w-px flex-none self-stretch bg-line" /> : null)
  // display: contents keeps the rail's flat a/.grp children (.sx-rail styles) and the tabs' one line
  return (
    <>
      {group(OVERVIEW)}
      {sep('s1')}
      <div data-testid="network-nav" role="group" aria-label={t('console.rail.network')} className="contents">
        {group(NETWORK)}
        {sep('s2')}
        {group(SERVICES)}
      </div>
      {sep('s3')}
      {group(ACCOUNT)}
    </>
  )
}

/** Up to 767: the rail as the header's menu — a button with the open section, the list under the header. */
function PhoneMenu({ path }: { path: string }) {
  const [open, setOpen] = useState(false)
  const btn = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLElement>(null)
  const { group, item } = current(path)
  useEffect(() => setOpen(false), [path])
  useEffect(() => {
    if (!open) return
    const down = (e: PointerEvent) => {
      const el = e.target as Node
      if (panel.current?.contains(el) || btn.current?.contains(el)) return
      setOpen(false)
    }
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      btn.current?.focus()
    }
    document.addEventListener('pointerdown', down)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('pointerdown', down)
      document.removeEventListener('keydown', key)
    }
  }, [open])
  return (
    <>
      <button
        ref={btn}
        type="button"
        className="sx-btn order-last min-h-[44px] w-full justify-between"
        aria-expanded={open}
        aria-controls="console-menu"
        data-testid="console-menu-button"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="sx-crumb">
          {group?.label && (
            <>
              {t(group.label as 'console.rail.network')}
              <Icon name="chevron-right" size={12} />
            </>
          )}
          <b>{item?.label ?? 'Overview'}</b>
        </span>
        <Icon name="chevron-down" size={16} />
      </button>
      {open && (
        <nav ref={panel} id="console-menu" aria-label="Main" className="sx-rail absolute inset-x-0 top-full z-50 !w-full border-b border-line shadow-pop">
          <NavItems path={path} />
        </nav>
      )}
    </>
  )
}

/** The way back to the cabinet: on the admin name a link there, on one name the cabinet's On air. */
function CabinetLink() {
  const { user } = useSession()
  const cls = 'sx-btn sm ghost max-md:min-h-[44px]'
  if (user?.area === 'admin' && user.app_url) {
    return (
      <a href={user.app_url} className={cls} data-testid="nav-cabinet">
        Cabinet ↗
      </a>
    )
  }
  return (
    <Link to="/" className={cls}>
      On air
    </Link>
  )
}

export function ConsoleShell({ status, guide }: { status: ReactNode; guide: ReactNode }) {
  const loc = useLocation()
  const path = loc.pathname
  const mode = useMode()
  const { group, item } = current(path)
  const tabs = useRef<HTMLElement>(null)
  // 768–1279: the current tab scrolls into view (LayoutBreakpoints «Tabs on a phone», F-22)
  useEffect(() => {
    tabs.current?.querySelector<HTMLElement>('[aria-current="page"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [path, mode])
  return (
    <div className="sx-console flex min-h-dvh flex-col">
      <header className="sx-top sticky top-0 z-40 !h-12 flex-none !gap-4 !px-4 max-md:!h-auto max-md:flex-wrap max-md:!gap-y-2 max-md:py-2" data-testid="console-top">
        {/* the console's lockup of RD-13: static logo + «Control» (the console shows no broadcast) */}
        <span className="flex flex-none">
          <ConsoleBrand />
        </span>
        {mode === 'rail' && (
          <span className="sx-crumb min-w-0" data-testid="console-crumb">
            {group?.label && (
              <>
                {t(group.label as 'console.rail.network')}
                <Icon name="chevron-right" size={12} />
              </>
            )}
            <b>{item?.label ?? 'Overview'}</b>
          </span>
        )}
        {mode === 'tabs' ? (
          <nav ref={tabs} aria-label="Main" className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto [scrollbar-width:none]">
            <NavItems path={path} tab />
          </nav>
        ) : (
          <span className="sp" />
        )}
        <div className="flex flex-none items-center gap-3">
          {status}
          <CabinetLink />
          <AppsButton />
          {guide}
          <AccountMenu />
        </div>
        {mode === 'menu' && <PhoneMenu path={path} />}
      </header>
      <LicenseBanner />
      <div className="flex min-h-0 flex-1">
        {mode === 'rail' && (
          <nav aria-label="Main" className="sx-rail sticky top-12 h-[calc(100dvh-48px)] self-start overflow-y-auto" data-testid="console-rail">
            <NavItems path={path} />
          </nav>
        )}
        <main className="min-w-0 flex-1 p-3 pb-12 md:p-4 md:pb-12">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
